import { useState, useCallback } from 'react';
import { useTranslation } from '@parentpath/i18n';
import { ArrowLeft, CheckCircle, AlertCircle, Loader2, Gift } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

export interface ValidatedPromoCode {
  code: string;
  codeType: 'beta' | 'promo';
  promoMonths: number | null; // null = lifetime
  isLifetime: boolean;
}

interface PromoCodeStepProps {
  onNext: (promoCode: ValidatedPromoCode | null) => void;
  onBack: () => void;
}

interface ValidationResult {
  valid: boolean;
  error?: string;
  code_type?: 'beta' | 'promo';
  promo_months?: number | null;
  is_lifetime?: boolean;
  remaining_uses?: number | null;
}

export function PromoCodeStep({ onNext, onBack }: PromoCodeStepProps) {
  const { t } = useTranslation('onboarding');
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Validate code when input changes
  const validateCode = useCallback(async (inputCode: string) => {
    const trimmedCode = inputCode.trim().toUpperCase();

    if (!trimmedCode || trimmedCode.length < 4) {
      setValidationResult(null);
      setError(null);
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      // Call the validate_promo_code function
      const { data, error: rpcError } = await supabase.rpc('validate_promo_code', {
        p_code: trimmedCode,
      });

      if (rpcError) {
        console.error('Validation RPC error:', rpcError);
        setError(t('promo.validationError'));
        setValidationResult(null);
        return;
      }

      const result = data as ValidationResult;
      setValidationResult(result);

      if (!result.valid) {
        setError(result.error || t('promo.invalidCode'));
      }
    } catch (err) {
      console.error('Validation error:', err);
      setError(t('promo.validationError'));
      setValidationResult(null);
    } finally {
      setIsValidating(false);
    }
  }, [t]);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = e.target.value.toUpperCase();
    setCode(newCode);
    setValidationResult(null);
    setError(null);

    // Debounce validation
    const timeoutId = setTimeout(() => {
      validateCode(newCode);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // Handle submit
  const handleSubmit = () => {
    if (!validationResult?.valid) {
      setError(t('promo.enterValid'));
      return;
    }

    // Pass validated code to parent
    onNext({
      code: code.trim().toUpperCase(),
      codeType: validationResult.code_type || 'promo',
      promoMonths: validationResult.promo_months || null,
      isLifetime: validationResult.is_lifetime || false,
    });
  };

  // Format duration text
  const getDurationText = (): string => {
    if (!validationResult?.valid) return '';

    if (validationResult.is_lifetime || validationResult.code_type === 'beta') {
      return t('promo.lifetime');
    }

    const months = validationResult.promo_months;
    if (!months) return t('promo.premiumAccess');

    if (months === 1) return t('promo.oneMonthFree');
    if (months === 12) return t('promo.oneYearFree');
    return t('promo.monthsFree', { months });
  };

  const isValid = validationResult?.valid === true;

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-6 bg-pp-orange/20 rounded-full flex items-center justify-center">
        <Gift className="w-8 h-8 text-pp-orange" />
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2 text-center">
        {t('promo.title')}
      </h1>

      {/* Description */}
      <p className="text-white/70 mb-6 text-center">
        {t('promo.description')}
      </p>

      {/* Code Input */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            value={code}
            onChange={handleInputChange}
            placeholder={t('promo.placeholder')}
            className={cn(
              'w-full px-4 py-4 text-center text-xl font-mono tracking-[0.2em] rounded-[10px] outline-none transition-colors uppercase',
              isValid
                ? 'border-2 border-green-500 bg-green-500/10 text-white'
                : error
                  ? 'border-2 border-red-400 bg-red-500/10 text-white'
                  : 'border-2 border-white/20 bg-white/10 text-white placeholder-white/30 focus:border-pp-orange'
            )}
          />
          {isValidating && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <Loader2 className="w-5 h-5 animate-spin text-white/50" />
            </div>
          )}
          {isValid && !isValidating && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
          )}
        </div>

        {/* Success Message */}
        {isValid && (
          <div className="mt-3 flex items-center justify-center gap-2 text-green-400">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">{getDurationText()}</span>
          </div>
        )}

        {/* Error Message */}
        {error && !isValidating && (
          <div className="mt-3 flex items-center justify-center gap-2 text-red-400">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}
      </div>

      {/* Info Box */}
      {validationResult?.valid && validationResult.code_type === 'beta' && (
        <div className="bg-purple-500/20 rounded-[10px] p-4 mb-6">
          <p className="text-sm text-white/80">
            <strong className="text-white">{t('promo.betaTitle')}</strong>
          </p>
          <ul className="mt-2 space-y-1 text-sm text-white/60">
            <li>{t('promo.betaFeature1')}</li>
            <li>{t('promo.betaFeature2')}</li>
            <li>{t('promo.betaFeature3')}</li>
          </ul>
        </div>
      )}

      {/* Buttons */}
      <div className="space-y-3">
        <button
          onClick={handleSubmit}
          disabled={!isValid || isValidating}
          className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {t('promo.submit')}
        </button>

        <button
          onClick={() => onNext(null)}
          className="w-full py-2 text-white/50 hover:text-white/70 text-sm transition-colors"
        >
          {t('promo.backOverview')}
        </button>
      </div>
    </div>
  );
}
